import {
  Toaster as ChakraToaster,
  Portal,
  Spinner,
  Stack,
  Toast,
  createToaster,
} from "@chakra-ui/react";
import React, { createContext, useContext, useState } from "react";

// Context for flyout state
interface FlyoutContextType {
  isFlyoutOpen: boolean;
  setIsFlyoutOpen: (open: boolean) => void;
  isFlyoutOpening: boolean;
  setIsFlyoutOpening: (opening: boolean) => void;
}

const FlyoutContext = createContext<FlyoutContextType | undefined>(undefined);

export const useFlyoutContext = () => {
  const context = useContext(FlyoutContext);
  if (!context) {
    throw new Error("useFlyoutContext must be used within a FlyoutProvider");
  }
  return context;
};

export const FlyoutProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false);
  const [isFlyoutOpening, setIsFlyoutOpening] = useState(false);

  return (
    <FlyoutContext.Provider
      value={{
        isFlyoutOpen,
        setIsFlyoutOpen,
        isFlyoutOpening,
        setIsFlyoutOpening,
      }}
    >
      {children}
    </FlyoutContext.Provider>
  );
};

// Create separate toasters for different placements
const leftToaster = createToaster({
  placement: "top-start",
  pauseOnPageIdle: true,
});

const rightToaster = createToaster({
  placement: "bottom-end",
  pauseOnPageIdle: true,
});

export const toaster = rightToaster; // Default toaster for backward compatibility

// Create a wrapper that delays toasts when flyout is opening
let flyoutContext: FlyoutContextType | undefined;

export const setFlyoutContext = (context: FlyoutContextType) => {
  flyoutContext = context;
};

export const createDelayedToast = (
  toastConfig: Parameters<typeof rightToaster.create>[0],
) => {
  if (flyoutContext?.isFlyoutOpening) {
    // Delay the toast if a flyout is opening
    setTimeout(() => {
      rightToaster.create(toastConfig);
    }, 600); // Wait for flyout opening to complete
  } else {
    rightToaster.create(toastConfig);
  }
};

export const Toaster = () => {
  const { isFlyoutOpen } = useFlyoutContext();

  // Use left placement when flyout is open, right placement otherwise
  const activeToaster = isFlyoutOpen ? leftToaster : rightToaster;

  return (
    <Portal>
      <ChakraToaster toaster={activeToaster} insetInline={{ mdDown: "4" }}>
        {(toast) => (
          <Toast.Root width={{ md: "sm" }}>
            {toast.type === "loading" ? (
              <Spinner size='sm' color='blue.solid' />
            ) : (
              <Toast.Indicator />
            )}
            <Stack gap='1' flex='1' maxWidth='100%'>
              {toast.title && <Toast.Title>{toast.title}</Toast.Title>}
              {toast.description && (
                <Toast.Description>{toast.description}</Toast.Description>
              )}
            </Stack>
            {toast.action && (
              <Toast.ActionTrigger>{toast.action.label}</Toast.ActionTrigger>
            )}
            {toast.meta?.closable && <Toast.CloseTrigger />}
          </Toast.Root>
        )}
      </ChakraToaster>
    </Portal>
  );
};

export default Toaster;
